import { Dispatch, RefObject, SetStateAction, useCallback, useMemo } from 'react';

import { TextField, TextFieldRef, ValidationResult, ValidationStatus } from 'components/inputs/text-field';
import { useSelectedLocation } from 'contexts';
import { getBaseUnit, getBookingByUnitId, getUnitByUnitNumber } from 'services';
import { Chassis, Container, EquipmentType, Genset } from 'types';
import { keepInspectedValues } from 'utils';
import { useNewTransaction } from '../new-transaction.context';

type OutboundUnitProps<T extends Chassis | Container | Genset> = {
  unit: T;
  setUnit: Dispatch<SetStateAction<T> | undefined>;
  required?: boolean;
  customError?: (unit: T | null) => string;
  textFieldRef?: RefObject<TextFieldRef>;
  customValidation?: (unit: T | null) => Promise<string | undefined>;
  ['data-cy']?: string;
};

const OutboundUnit = <T extends Chassis | Container | Genset>({
  unit,
  setUnit,
  required,
  customValidation,
  textFieldRef,
  ...props
}: OutboundUnitProps<T>) => {
  const { chassis, container, genset, stack } = useNewTransaction();
  const { selectedLocation } = useSelectedLocation();

  const isBaseChassis: boolean = useMemo(() => {
    return !!unit.mountedUnits?.some(({ equipmentType }) => equipmentType === EquipmentType.CONTAINER);
  }, [unit.mountedUnits]);

  const isMountedUnit: boolean = useMemo(() => {
    return unit.equipmentType !== EquipmentType.CONTAINER && !!unit.baseUnit;
  }, [unit.equipmentType, unit.baseUnit]);

  const allUnitNumbers = useMemo(() => {
    return [chassis, container, genset, ...stack].map(({ unitNumber }) => unitNumber);
  }, [chassis, container, genset, stack]);

  const validateUnit = useCallback(
    async (unitNumber: string): Promise<ValidationResult> => {
      let error: string | undefined;

      if (!unitNumber) {
        error = await customValidation?.(null);
        return { status: ValidationStatus.DEFAULT, error };
      } else if (allUnitNumbers.filter(un => un === unitNumber).length > 1) {
        return { status: ValidationStatus.WARNING, error: `Duplicate unit number` };
      }

      const response = (await getUnitByUnitNumber(unitNumber)) as T | null;
      if (response?.id) {
        response.baseUnit = await getBaseUnit(response.id);
        response.booking = (await getBookingByUnitId(response.id)) ?? unit.booking;
      }

      error = await customValidation?.(response);
      if (error) {
        return { status: ValidationStatus.WARNING, error };
      } else if (!response || response.location?.id !== selectedLocation?.id) {
        setUnit(u => ({ ...u, id: null, baseUnit: null, booking: null }));
        return {
          status: ValidationStatus.WARNING,
          error: !response?.location?.name ? 'Unit not in yard' : `Unit currently located at ${response.location.name}`
        };
      } else if (response.onHold) {
        return { status: ValidationStatus.WARNING, error: 'HOLD UNIT' };
      } else if (response.equipmentType !== unit.equipmentType) {
        return { status: ValidationStatus.WARNING, error: `Unit not ${unit.equipmentType?.toLowerCase()}` };
      }
      /* re-validating the unit already here (e.g. back from review) keeps what was recorded on this move */
      setUnit(u => keepInspectedValues(u, response));
      return { status: ValidationStatus.SUCCESS };
    },
    [allUnitNumbers, selectedLocation?.id, unit.booking, unit.equipmentType, customValidation, setUnit]
  );

  return (
    <TextField
      disabled={isBaseChassis || isMountedUnit}
      label="UNIT NUMBER"
      ref={textFieldRef}
      required={required}
      value={unit.unitNumber ?? ''}
      onChange={unitNumber => setUnit(!unitNumber ? undefined : u => ({ ...u, unitNumber }))}
      validate={validateUnit}
      data-cy={props['data-cy']}
    />
  );
};

export default OutboundUnit;
